import { ArrowRightIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import Skeleton from '@/components/UI/Skeleton';
import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';
import { useLocale } from '@/renderer/utils/locale';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

type SportBlock = [API.Id[], API.ReleaseStats['seasonId'][1] | undefined, string][];

interface StatsBlock {
  cumulative: API.ReleaseStats['seasonId'][1];
  seasons: API.ReleaseStats['seasonId'];
  firstSeasonId: API.Id;
  lastSeasonId: API.Id;
  firstSeasonDate: number;
  lastSeasonDate: number;
  last4Ids: API.Id[];
  last4: API.ReleaseStats['seasonId'][1][];
  seasonsMeta: Record<API.Id, Partial<API.Season>>;
  sportMeta: Partial<API.Sport>;
}

const getStats: RequestSchema<API.ReleaseStats> = {
  input: '/api/v1/release/stats',
};

export default PicksArchivePage;

const baseStats = {
  win: 0,
  loss: 0,
  pending: 0,
  push: 0,
  canceled: 0,
  noAction: 0,
  risk: 0,
  units: 0,
  total: 0,
};

const seasonPaths = {
  'Current season': '',
  'Last season': '/season/last',
  'Last 3 seasons': '/season/last-3',
  'All time': '/season/all-time',
};

const firstYear = (str?: string) => str?.split?.('-')?.[0] || '?';
const lastYear = (str?: string) => {
  const split = str?.split?.('-');
  return split?.[1] || split?.[0] || '?';
};

function PicksArchive() {
  const statsRequest = useRequest(
    getStats,
    useMemo(
      () => ({
        query: {
          page: { limit: 100 },
          data: {
            playType: ['side', 'total', 'prop', 'future', 'not-available'],
          },
        },
        cache: { maxAge: 5 * 60 * 1000 },
      }),
      [],
    ),
  );

  const isLoading = statsRequest.loading;

  const stats = useMemo(() => {
    const results: Record<API.Id, StatsBlock> = {};

    if (statsRequest?.data?.seasonId) {
      for (const seasonKey in statsRequest.data.seasonId) {
        const season = statsRequest.data.seasonId[seasonKey];

        const metaSport = season.meta.sport?.[0]
          ? season.meta.sport?.[0]
          : Object.values(season.meta.sport)[0];
        const metaSeason = season.meta.season?.[0]
          ? season.meta.season?.[0]
          : Object.values(season.meta.season)[0];

        let sportId = metaSeason?.sportId || metaSport?.id;
        let seasonId = metaSeason?.id;
        const seasonStats = season.stats;
        let sportMeta: Partial<API.Sport> = {};

        if (season.meta.sport) {
          const metaSports = Object.values(season.meta.sport) as API.Sport[];
          if (metaSports.length === 1) {
            sportMeta = metaSports[0] as API.Sport;
            sportId = sportMeta?.id;
          }
        }

        if (metaSeason?.type === 'plus') {
          // for some reason the Plus season isn't included in meta
          sportMeta = {
            ...metaSeason,
            id: 9,
            name: 'RAS+',
            shortName: 'plus',
          };
          sportId = 9;
        }

        if (sportId !== 1 && sportId !== 2 && sportId !== 3 && sportId !== 9) {
          continue; // we only care about CFB, CBB, NFL
        }

        if (!sportId || sportId < 1) {
          continue; // skip these ones
        }

        // Initialize result set by sportId
        if (!results[sportId]) {
          results[sportId] = {
            cumulative: { ...baseStats },
            seasons: {},
            firstSeasonId: Infinity,
            lastSeasonId: -Infinity,
            firstSeasonDate: Infinity,
            lastSeasonDate: -Infinity,
            last4Ids: [],
            last4: [],
            seasonsMeta: {},
            sportMeta,
          };
        }

        const sportRef = results[sportId];

        let seasonDate = 0;

        if (season.meta.season) {
          const seasonMeta = Object.values(season.meta.season)[0];

          if (seasonMeta) {
            seasonId = seasonMeta.id;

            if (!seasonId || seasonId < 1) {
              continue; // skip these ones
            }

            seasonDate = Date.parse(seasonMeta.startDate || seasonMeta.endDate);

            sportRef.seasonsMeta[seasonId] = seasonMeta;
          }
        }

        if (!seasonId || seasonId < 1) {
          continue; // skip these ones
        }

        // Initialize result set season stats inside sportId
        if (!sportRef.seasons[seasonId]) {
          sportRef.seasons[seasonId] = { ...baseStats };
        }

        const seasonRef = sportRef.seasons[seasonId];

        let islast4 = false;

        // is this one of the last 4 seasons?
        if (sportRef.last4Ids.length < 4) {
          sportRef.last4Ids.push(seasonId);

          sportRef.last4Ids.sort((aSeasonId, bSeasonId) => {
            const aSeasonDate = Date.parse(
              sportRef.seasonsMeta[aSeasonId].startDate || sportRef.seasonsMeta[aSeasonId].endDate,
            );
            const bSeasonDate = Date.parse(
              sportRef.seasonsMeta[bSeasonId].startDate || sportRef.seasonsMeta[bSeasonId].endDate,
            );
            return aSeasonDate > bSeasonDate ? 1 : aSeasonDate < bSeasonDate ? -1 : 0;
          });

          const injectIndex = sportRef.last4Ids.indexOf(seasonId);
          sportRef.last4.splice(injectIndex + 1, 0, { ...baseStats });

          islast4 = true;
        } else if (
          Date.parse(
            sportRef.seasonsMeta[sportRef.last4Ids[0]].startDate ||
              sportRef.seasonsMeta[sportRef.last4Ids[0]].endDate,
          ) < seasonDate
        ) {
          sportRef.last4Ids.shift();
          sportRef.last4Ids.push(seasonId);

          sportRef.last4.shift();

          sportRef.last4.push({ ...baseStats });

          const joinedSeasons = sportRef.last4Ids
            .map((seasonId, idx) => ({ seasonId: seasonId, values: sportRef.last4[idx] }))
            .sort(({ seasonId: aSeasonId }, { seasonId: bSeasonId }) => {
              const aSeasonDate = Date.parse(
                sportRef.seasonsMeta[aSeasonId].startDate ||
                  sportRef.seasonsMeta[aSeasonId].endDate,
              );
              const bSeasonDate = Date.parse(
                sportRef.seasonsMeta[bSeasonId].startDate ||
                  sportRef.seasonsMeta[bSeasonId].endDate,
              );
              return aSeasonDate > bSeasonDate ? 1 : aSeasonDate < bSeasonDate ? -1 : 0;
            });

          sportRef.last4Ids = joinedSeasons.map((item) => item.seasonId);
          sportRef.last4 = joinedSeasons.map((item) => item.values);

          islast4 = true;
        }

        // Is this the first or last season?
        if (seasonDate > sportRef.lastSeasonDate) {
          sportRef.lastSeasonDate = seasonDate;
          sportRef.lastSeasonId = seasonId;
        }

        if (seasonDate < sportRef.firstSeasonDate) {
          sportRef.firstSeasonDate = seasonDate;
          sportRef.firstSeasonId = seasonId;
        }

        for (const result in seasonStats) {
          switch (result) {
            case 'win':
            case 'loss':
            case 'pending':
            case 'push':
            case 'noAction':
            case 'canceled': {
              const resultStats = seasonStats[result];

              seasonRef[result] += resultStats.total;
              sportRef.cumulative[result] += resultStats.total;

              seasonRef.risk += season.risk;
              seasonRef.units += season.units;
              seasonRef.total += season.total;

              if (islast4) {
                sportRef.last4[sportRef.last4.length - 1][result] += resultStats.total;

                sportRef.last4[sportRef.last4.length - 1].risk += resultStats.risk;
                sportRef.last4[sportRef.last4.length - 1].units += resultStats.units;
                sportRef.last4[sportRef.last4.length - 1].total += resultStats.total;
              }
            }
          }
        }

        sportRef.cumulative.risk += season.risk;
        sportRef.cumulative.units += season.units;
        sportRef.cumulative.total += season.total;
      }

      return results;
    }

    return undefined;
  }, [statsRequest?.data]);

  const blocks = useMemo(() => {
    const results: Record<API.Id, SportBlock> = {};

    for (const sportId of [1, 2, 3, 9]) {
      const sportStats = stats?.[sportId];

      const currentSeasonId = sportStats?.last4Ids[sportStats?.last4Ids.length - 1];
      const currentSeason = sportStats?.last4[sportStats?.last4.length - 1];
      const lastSeasonId = sportStats?.last4Ids[sportStats?.last4Ids.length - 2];
      const lastSeason = sportStats?.last4[sportStats?.last4.length - 2];

      const sportPrev3Ids = sportStats?.last4Ids ? sportStats.last4Ids.slice(-3) : [];
      const sportPrev3 = sportStats?.last4 ? sportStats.last4.slice(-3) : [];

      if (sportPrev3Ids.length === sportStats?.last4Ids.length) {
        // not enough seasons, remove one more @todo why did we do this? commented out
        // sportPrev3Ids.length -= 1;
        // sportPrev3.length -= 1;
      }

      const sportLast3Cumulative = sportPrev3?.reduce((obj, season) => {
        for (const result in season) {
          if (obj[result]) {
            obj[result] += season[result];
          } else {
            obj[result] = season[result];
          }
        }

        return obj as typeof lastSeason;
      }, {});

      results[sportId] = [
        [[currentSeasonId], currentSeason, 'Current season'],
        [[lastSeasonId], lastSeason, 'Last season'],
        [sportPrev3Ids, sportLast3Cumulative, `Last ${sportPrev3Ids.length} seasons`],
        [[sportStats?.firstSeasonId, sportStats?.lastSeasonId], sportStats?.cumulative, 'All time'],
      ];

      if (sportId === 1 || sportId === 2 || sportId === 3) {
        results[9999] = [
          [[currentSeasonId], currentSeason, 'Current season'],
          [[lastSeasonId], lastSeason, 'Last season'],
          [sportPrev3Ids, sportLast3Cumulative, `Last ${sportPrev3Ids.length} seasons`],
          [
            [sportStats?.firstSeasonId, sportStats?.lastSeasonId],
            sportStats?.cumulative,
            'All time',
          ],
        ];
      }
    }

    return results;
  }, [stats]);

  const rasService = useMemo(() => {
    const result: {
      sportId: API.Id;
      sportMeta: Partial<API.Sport>;
      block: SportBlock;
      stat?: StatsBlock;
    } = {
      sportId: 65535,
      sportMeta: {
        name: 'RAS Service Sports',
        shortName: 'RAS',
      },
      block: [],
    };

    function isObject(item: any) {
      return !!item && typeof item === 'object' && !Array.isArray(item);
    }

    function mergeAndSum<A extends Record<any, any>>(obj1: A, obj2: A) {
      const result: any = {};
      const keys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);

      for (const key of keys) {
        const val1 = obj1[key as keyof A];
        const val2 = obj2[key as keyof A];

        if (typeof val1 === 'number' && typeof val2 === 'number') {
          if (key.toString().match(/(Id|^id|Date|Time|At)$/)) {
            // don't sum these ones
            if (key.startsWith('first') && val2 < val1) {
              result[key] = val2; // firstSeasonDate/Id
            } else if (key.startsWith('last') && val2 > val1) {
              result[key] = val2; // lastSeasonDate/Id
            } else {
              result[key] = val1;
            }
          } else {
            result[key] = val1 + val2;
          }
        } else if (isObject(val1) && isObject(val2)) {
          result[key] = mergeAndSum(val1, val2);
        } else {
          result[key] = val1 !== undefined ? val1 : val2;
        }
      }

      return result as A;
    }

    for (const sportId of [3, 1, 2]) {
      const sportBlock = blocks[sportId];
      const sportStat = stats?.[sportId];

      if (sportBlock) {
        for (const key in sportBlock) {
          const sportBlockBlock = sportBlock[key];

          if (!result.block[key]) {
            result.block[key] = [...sportBlockBlock];
          } else {
            const curSeason = result.block[key][1];
            const nextSeason = sportBlockBlock[1];
            result.block[key][1] = mergeAndSum(result.block[key][1], sportBlockBlock[1]);
          }
        }
        result.block.push();
      }

      if (sportStat) {
        if (!result.stat) {
          result.stat = { ...sportStat };
        } else {
          // merge
          if (result.stat.firstSeasonDate > sportStat.firstSeasonDate) {
            result.stat.seasonsMeta = {
              ...result.stat.seasonsMeta,
              [result.stat.firstSeasonId]: {
                ...result.stat.seasonsMeta[result.stat.firstSeasonId],
              },
            };
            result.stat.firstSeasonDate = sportStat.firstSeasonDate;
            result.stat.firstSeasonId = sportStat.firstSeasonId;
          }

          if (result.stat.lastSeasonDate > sportStat.lastSeasonDate) {
            result.stat.lastSeasonDate = sportStat.lastSeasonDate;
            result.stat.lastSeasonId = sportStat.lastSeasonId;
          }

          result.stat = mergeAndSum(result.stat, sportStat);
        }
      }
    }

    return result;
  }, [blocks, stats]);

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <Header title="All Results" className="py-2 md:hidden" />
      <div className="mx-auto w-full overflow-auto px-4 py-8 lg:px-8 sm:px-6">
        <div className="mx-auto w-full max-w-7xl">
          <div className="px-4 lg:px-8 sm:px-6">
            <div className="relative mx-auto max-w-2xl pb-8 text-center md:pt-20 md:pb-24">
              <h1 className="hidden font-bold text-3xl text-zinc-900 tracking-tight md:block dark:text-zinc-200 sm:text-4xl">
                All Results
              </h1>
              <p className="mt-4 text-left text-base text-zinc-600 leading-7 md:text-center dark:text-zinc-400">
                All records are graded against a conservative widely available consensus line from
                sharp, market-leading sportsbooks and not until most common PPH skins have posted.
                No openers, rogues, overnights, or stale lines are ever used. Since 2020, all RAS
                results have been independently monitored by{' '}
                <a href="https://betstamp.app/u/RAS" target="_blank" rel="noreferrer">
                  betstamp
                </a>
                .
              </p>
            </div>
          </div>
          {/* <a href="https://betstamp.app/u/RAS" target="_blank" rel="noreferrer">
            <div className="rounded-sm bg-sky-50 p-4 text-sm text-sky-800 ring-sky-900/10 hover:text-sky-900 dark:bg-sky-900/50 dark:text-sky-200 dark:hover:text-sky-100">
              <div className="flex space-x-2 items-center">
                <CheckBadgeIcon height={24} className="size-6 shrink-0 text-sky-500" />
                <div>
                  <span>Since 2020, all RAS results have been independently monitored by</span>
                  <img
                    src={srcBetstamp}
                    className="mx-auto inline-block ml-1 h-6 relative shrink-0 pb-px"
                  />
                  <span>.</span>
                </div>
              </div>
            </div>
          </a> */}
          <PicksArchiveSection
            idx={9999}
            sportMeta={rasService.sportMeta}
            sportId={rasService.sportId}
            isLoading={isLoading}
            block={rasService.block}
            stat={rasService.stat}
          />
          {[3, 1, 2, 9].map((sportId, idx) => {
            const sportMeta = stats?.[sportId]?.sportMeta;

            return (
              <PicksArchiveSection
                idx={idx}
                sportMeta={sportMeta}
                sportId={sportId}
                isLoading={isLoading}
                block={blocks[sportId]}
                stat={stats?.[sportId]}
              />
            );
          })}
        </div>
        <Footer />
      </div>
    </div>
  );
}

function PicksArchiveSection({
  sportMeta,
  sportId,
  isLoading,
  idx,
  block,
  stat,
}: {
  sportMeta?: Partial<API.Sport>;
  sportId?: API.Id;
  isLoading: boolean;
  idx: number;
  block: SportBlock;
  stat?: StatsBlock;
}) {
  const { formatNumber } = useLocale();

  return (
    <section className="mt-12" key={`${sportId}-${idx}`}>
      <div className="flex flex-row items-baseline justify-between">
        <div className="font-semibold text-lg/7 text-zinc-950 tracking-[-0.015em] dark:text-white sm:text-2xl/7">
          <Skeleton className="inline-block leading-none" loading={isLoading}>
            {sportMeta?.name || 'Loading...'}
          </Skeleton>
        </div>
        {/* <a
          className="flex flex-row items-center space-x-2 rounded-sm bg-zinc-900 px-2.5 py-1.5 text-sm text-white hover:bg-zinc-700 hover:text-white dark:bg-zinc-500"
          href={sportId ? `/archive?sportId=${sportId}` : '#'}
        >
          <span>{sportMeta?.name || '(Loading...)'} results</span>
        </a> */}
      </div>

      <aside className="mt-4 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {block.map(([seasonIds, nums, title], idx2) => {
          const firstSeasonMeta = stat?.seasonsMeta[seasonIds[0]];
          const lastSeasonMeta = stat?.seasonsMeta[seasonIds[seasonIds.length - 1]];
          const lastSeasonNameParts =
            `${lastSeasonMeta?.name || firstSeasonMeta?.name || ''}`.split('-');
          const lastSeasonNameRegular = lastSeasonNameParts[1] || lastSeasonNameParts[0];
          const [firstSeasonNameRegular] =
            `${firstSeasonMeta?.name || lastSeasonMeta?.name || ''}`.split('-');

          const firstSeasonName =
            sportId === 9
              ? `${(firstSeasonMeta?.name || lastSeasonMeta?.name || '').split('/')[0]} ${firstYear((firstSeasonMeta?.name || lastSeasonMeta?.name || '').split(' ')[1])}`
              : firstSeasonNameRegular;

          const lastSeasonPart = (lastSeasonMeta?.name || firstSeasonMeta?.name || '').match(
            /^[A-Za-z]+\/([A-Za-z]+) (.*)/,
          );
          const lastSeasonName =
            sportId === 9
              ? `${lastSeasonPart?.[1]} ${lastYear(lastSeasonPart?.[2] || lastSeasonMeta?.name || firstSeasonMeta?.name || '')}`
              : lastSeasonNameRegular;
          const sportShortName =
            sportMeta?.shortName?.toLowerCase() || sportMeta?.name?.toLowerCase();

          return (
            <a
              className="group rounded-sm bg-linear-to-t from-zinc-900/2.5 to-transparent p-4 text-left font-semibold text-zinc-950 ring-1 ring-zinc-900/10 dark:bg-white/5 dark:hover:bg-white/10 hover:from-zinc-900/5 sm:p-6 sm:py-8 dark:hover:text-white dark:text-white hover:text-zinc-950 hover:shadow-xs dark:ring-white/20"
              key={`${firstSeasonMeta?.id}-${lastSeasonMeta?.id}-${idx2}`}
              href={`/archive/${sportShortName || ''}${seasonPaths?.[title] || ''}`}
            >
              <div className="flex flex-row items-center justify-between">
                <p className="font-normal text-lg">{title}</p>
                <ChevronRightIcon
                  height={20}
                  className="size-5 shrink-0 group-hover:hidden"
                  aria-hidden="true"
                />
                <ArrowRightIcon
                  height={20}
                  className="hidden size-5 shrink-0 group-hover:inline-block"
                  aria-hidden="true"
                />
              </div>
              <span className="pb-4 font-normal text-sm text-zinc-500 capitalize tracking-wider dark:text-zinc-300">
                <Skeleton className="inline-block leading-none" loading={isLoading}>
                  {(firstSeasonName === lastSeasonName
                    ? firstSeasonName
                    : `${firstSeasonName}-${lastSeasonName}`) || 'Loading...'}
                </Skeleton>
              </span>
              <div className="mt-4 grid grid-cols-2 gap-2 sm:mt-6 lg:grid-cols-2 sm:grid-cols-1 xl:grid-cols-1 sm:gap-4">
                <div className="flex flex-col text-base">
                  <span className="font-normal text-xs text-zinc-500 dark:text-zinc-400">
                    Record
                  </span>
                  <span className="text-xl">
                    <Skeleton className="inline-block leading-none" loading={isLoading}>
                      {nums?.win || 0}-{nums?.loss || 0}-{nums?.push || 0}
                    </Skeleton>
                  </span>
                </div>
                <div className="flex flex-col text-base">
                  <span className="font-normal text-xs text-zinc-500 dark:text-zinc-400">
                    Win rate
                  </span>
                  <span className="text-xl">
                    <Skeleton className="inline-block leading-none" loading={isLoading}>
                      {formatNumber(
                        (nums?.win || 0) / ((nums?.win || 0) + (nums?.loss || 0)) || 0,
                        {
                          style: 'percent',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </Skeleton>
                  </span>
                </div>
                <div className="flex flex-col text-base">
                  <span className="font-normal text-xs text-zinc-500 dark:text-zinc-400">
                    Units
                  </span>
                  <span
                    className="text-emerald-600 text-xl dark:text-emerald-400 dark:data-[negative=true]:text-red-400 data-[negative=true]:text-red-600"
                    data-negative={nums?.units < 0}
                  >
                    <Skeleton className="inline-block leading-none" loading={isLoading}>
                      {nums?.units < 0 ? '' : '+'}
                      {formatNumber(nums?.units || 0, {
                        style: 'decimal',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Skeleton>
                  </span>
                </div>
              </div>
            </a>
          );
        })}
      </aside>
    </section>
  );
}

// Main component
function PicksArchivePage() {
  return <PicksArchive />;
}
